import { Button, Code, FormControl, FormLabel, Heading, Icon, Link, Select, Stack } from '@chakra-ui/react'
import type { GetTeamsResponse, TeamBase } from '@clsplus/api-types/api-admin'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { Link as RouterLink, useLoaderData, useSearchParams } from 'react-router-dom'

import { organisationsRefDataQuery } from '../../../api/get-organisations-ref-data'
import { teamsQuery } from '../../../api/get-teams'
import { ListPageFilters } from '../../../components/list-page-filters'
import { ComboBox } from '../../../ui/combo-box'
import { Input } from '../../../ui/input'
import { NonIdealState } from '../../../ui/non-ideal-state'
import { Table } from '../../../ui/table'

type TeamsFilters = {
  deleted: string
  id: string
  intId: string
  name: string
  organisationId: string
}

export const loader =
  (queryClient: QueryClient) =>
  async ({ request }: LoaderFunctionArgs) => {
    const urlParams = Object.fromEntries(new URL(request.url).searchParams)
    const teamsQueryOpts = teamsQuery(urlParams)

    const [initialOrganisationsRefData, initialTeamsData] = await Promise.all([
      queryClient.ensureQueryData(organisationsRefDataQuery),
      queryClient.getQueryData<InfiniteData<GetTeamsResponse>>(teamsQueryOpts.queryKey) ??
        (await queryClient.fetchInfiniteQuery(teamsQueryOpts)),
    ])

    return {
      initialOrganisationsRefData,
      initialTeamsData,
    }
  }

export default function Teams() {
  const [searchParams, setSearchParams] = useSearchParams()

  const params = Object.fromEntries(searchParams)
  const paramsCount = Object.keys(params).length

  const { initialOrganisationsRefData, initialTeamsData } = useLoaderData() as Awaited<
    ReturnType<ReturnType<typeof loader>>
  >

  const { data: teams, ...teamsQueryResult } = useInfiniteQuery({
    ...teamsQuery(params),
    initialData: initialTeamsData,
  })

  const { data: organisationsRefData } = useQuery({
    ...organisationsRefDataQuery,
    initialData: initialOrganisationsRefData,
  })

  const filtersFormDefaultValues = {
    deleted: params.deleted ?? '',
    id: params.id ?? '',
    intId: params.intId ?? '',
    name: params.name ?? '',
    organisationId: params.organisationId ?? '',
  }

  const { control, register, handleSubmit, reset } = useForm<TeamsFilters>({
    values: filtersFormDefaultValues,
  })

  const handleDismissFilters = () => reset(filtersFormDefaultValues)

  const handleSubmitClick = (formData: TeamsFilters) => {
    const newSearchParams = Object.entries(formData).reduce<Record<string, string>>(
      (acc, [key, val]) => ({
        ...(val && { [key]: val }),
        ...acc,
      }),
      {}
    )

    setSearchParams(newSearchParams)
  }

  const handleResetClick = () => {
    reset(filtersFormDefaultValues)
    setSearchParams({})
  }

  const rows = teams?.pages.flatMap(page => [...page.data])

  const columns: ColumnDef<TeamBase>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link as={RouterLink} to={row.original.id}>
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'organisation',
        accessorFn: row => row.organisation.name,
        header: 'Organisation',
      },
      {
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'intId',
        accessorFn: row => row.integrationIds.SDS,
        header: 'SDS ID',
      },
      {
        accessorKey: 'deleted',
        accessorFn: row => (row.isDeleted ? 'true' : 'false'),
        header: 'Deleted',
        cell: ({ row }) => (row.original.isDeleted ? 'Yes' : 'No'),
      },
    ],
    []
  )

  const organisationOptions = organisationsRefData.map(organisation => ({
    value: organisation.id,
    label: organisation.name,
  }))

  return (
    <>
      <Heading as="h2" size="lg">
        Teams
      </Heading>
      <Stack w="100%" spacing={8} my={6}>
        <ListPageFilters
          formId="teams-filters"
          onDismiss={handleDismissFilters}
          onReset={handleResetClick}
          onSubmit={handleSubmit(handleSubmitClick)}
          title="Filter teams"
        >
          <FormControl>
            <FormLabel>Team ID</FormLabel>
            <Input {...register('id')} />
          </FormControl>
          <FormControl>
            <FormLabel>SDS ID</FormLabel>
            <Input {...register('intId')} />
          </FormControl>
          <FormControl>
            <FormLabel>Organisation</FormLabel>
            <Controller
              control={control}
              name="organisationId"
              render={({ field: { onChange, value } }) => (
                <ComboBox
                  isSingleSelect
                  options={organisationOptions}
                  onChange={onChange}
                  selectedOptions={organisationOptions.filter(option => option.value === value)}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input {...register('name')} />
          </FormControl>
          <FormControl>
            <FormLabel>Deleted</FormLabel>
            <Select {...register('deleted')}>
              <option value="">-</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </FormControl>
        </ListPageFilters>
        {teamsQueryResult.isError ? (
          <NonIdealState
            title="Error fetching teams"
            description="Here's what we know:"
            iconColor="red.600"
            icon={['fad', 'triangle-exclamation']}
          >
            <Code mt={4} px={6} py={4} borderRadius="6px" bg="primary.600">
              {teamsQueryResult.error instanceof Error
                ? teamsQueryResult.error.message
                : 'An unknown error occurred. Please contact a system administrator.'}
            </Code>
            <Button
              mt={4}
              variant="secondary"
              leftIcon={<Icon as={FontAwesomeIcon} icon={['fas', 'xmark-large']} />}
              onClick={handleResetClick}
            >
              Reset filters
            </Button>
          </NonIdealState>
        ) : rows?.length ? (
          <Table columns={columns} data={rows} />
        ) : (
          <NonIdealState
            title="No teams found"
            description={
              paramsCount > 0 ? "We couldn't find any teams matching your filters" : "We couldn't find any teams"
            }
            iconColor="primary.400"
            icon={['fas', 'face-confused']}
          >
            {paramsCount > 0 && (
              <Button
                mt={4}
                variant="secondary"
                leftIcon={<Icon as={FontAwesomeIcon} icon={['fas', 'xmark-large']} />}
                onClick={handleResetClick}
              >
                Reset filters
              </Button>
            )}
          </NonIdealState>
        )}
        {teamsQueryResult.isSuccess && teamsQueryResult.hasNextPage && (
          <Button
            mt={8}
            variant="primary"
            leftIcon={<Icon as={FontAwesomeIcon} icon={['fas', 'ellipsis']} />}
            isLoading={teamsQueryResult.isFetchingNextPage}
            onClick={() => teamsQueryResult.fetchNextPage()}
          >
            Load more
          </Button>
        )}
      </Stack>
    </>
  )
}
